import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Filter as FilterIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TarefaModal from "../components/TarefaModal";
import { useAuth } from "../context/AuthContext";
import {
  adminService,
  clienteService,
  tarefaService,
  usuarioService,
} from "../services/api";

const Tarefas = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tarefas, setTarefas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [escritorios, setEscritorios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTarefa, setEditingTarefa] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Estado para confirmação de exclusão
  const [confirmacaoExclusao, setConfirmacaoExclusao] = useState({
    aberto: false,
    tarefaId: null,
    tarefaDescricao: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Estado do formulário
  const [formData, setFormData] = useState({
    cliente_id: "",
    descricao: "",
    data_vencimento: new Date(),
    hora_vencimento: new Date(),
    status: "pendente",
    responsavel_id: "",
    priorizar: false,
    comentarios: "",
  });

  // Estados dos filtros
  const [filters, setFilters] = useState({
    cliente_id: "",
    status: "pendente", // Filtro padrão para tarefas pendentes
    data_inicio: "",
    data_fim: "",
    priorizar: "",
    responsavel_id: "",
    escritorio_id_filter: "", // Novo filtro para super admin
  });

  // Estado da paginação
  const [paginacao, setPaginacao] = useState({
    page: 0,
    limit: 10,
    total: 0,
  });

  const carregarTarefas = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: paginacao.page + 1,
        limit: paginacao.limit,
        ...filters,
      };
      console.log("Parâmetros enviados:", params);

      const response = await tarefaService.getAll(params);
      console.log("Resposta do backend:", response);

      setTarefas(response.data || []);
      setPaginacao((prev) => ({
        ...prev,
        total: response.pagination?.total || 0,
      }));
    } catch (error) {
      console.error("Erro ao carregar tarefas:", error);
      mostrarSnackbar("Erro ao carregar tarefas", "error");
    } finally {
      setLoading(false);
    }
  }, [paginacao.page, paginacao.limit, filters]);

  const carregarClientes = useCallback(async () => {
    try {
      const response = await clienteService.getAll({ ativo: true });
      setClientes(response.clientes || []);
    } catch (error) {
      console.error("Erro ao carregar clientes:", error);
    }
  }, []);

  const carregarUsuarios = useCallback(async () => {
    try {
      const response = await usuarioService.getAll();
      setUsuarios(response.usuarios || response.data || []);
    } catch (error) {
      console.error("Erro ao carregar usuários:", error);
    }
  }, []);

  const carregarEscritorios = useCallback(async () => {
    try {
      const response = await adminService.getEscritorios();
      // A API retorna diretamente um array, não um objeto com propriedade escritorios
      setEscritorios(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Erro ao carregar escritórios:", error);
    }
  }, []);

  useEffect(() => {
    carregarTarefas();
    carregarClientes();
    carregarUsuarios();
    if (user?.is_super_admin) {
      carregarEscritorios();
    }
  }, [
    carregarTarefas,
    carregarClientes,
    carregarUsuarios,
    carregarEscritorios,
    user?.is_super_admin,
  ]);

  // Recarregar tarefas quando filtros mudarem
  useEffect(() => {
    if (paginacao.page === 0) {
      carregarTarefas();
    }
  }, [filters, paginacao.page, carregarTarefas]);

  // Detectar query parameter para edição
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const editId = urlParams.get("edit");

    if (editId) {
      // Primeiro, tentar encontrar a tarefa na lista atual
      const tarefaParaEditar = tarefas.find((t) => t.id === parseInt(editId));

      if (tarefaParaEditar) {
        // Preencher o formulário e abrir o modal
        setFormData({
          cliente_id: tarefaParaEditar.cliente_id,
          descricao: tarefaParaEditar.descricao,
          data_vencimento: new Date(
            tarefaParaEditar.data_vencimento + "T00:00:00",
          ),
          hora_vencimento: new Date(
            `2000-01-01T${tarefaParaEditar.hora_vencimento}`,
          ),
          status: tarefaParaEditar.status,
          responsavel_id: tarefaParaEditar.responsavel_id || "",
          priorizar: tarefaParaEditar.priorizar || false,
          comentarios: tarefaParaEditar.comentarios || "",
        });
        setEditingTarefa(tarefaParaEditar);
        setOpenDialog(true);

        // Limpar o query parameter da URL
        navigate(location.pathname, { replace: true });
      } else {
        // Se não encontrou na lista atual, buscar diretamente pela API
        const buscarTarefaEspecifica = async () => {
          try {
            const response = await tarefaService.getById(parseInt(editId));

            // Tentar diferentes estruturas de resposta
            const tarefa =
              response.data?.tarefa ||
              response.tarefa ||
              response.data ||
              response;

            if (tarefa && tarefa.id) {
              // Preencher o formulário e abrir o modal
              setFormData({
                cliente_id: tarefa.cliente_id,
                descricao: tarefa.descricao,
                data_vencimento: new Date(tarefa.data_vencimento + "T00:00:00"),
                hora_vencimento: new Date(
                  `2000-01-01T${tarefa.hora_vencimento}`,
                ),
                status: tarefa.status,
                responsavel_id: tarefa.responsavel_id || "",
                priorizar: tarefa.priorizar || false,
                comentarios: tarefa.comentarios || "",
              });
              setEditingTarefa(tarefa);
              setOpenDialog(true);

              // Limpar o query parameter da URL
              navigate(location.pathname, { replace: true });
            } else {
              mostrarSnackbar("Tarefa não encontrada", "error");
            }
          } catch (error) {
            console.error("Erro ao buscar tarefa específica:", error);
            mostrarSnackbar("Erro ao carregar tarefa para edição", "error");
            // Limpar o query parameter da URL mesmo em caso de erro
            navigate(location.pathname, { replace: true });
          }
        };

        buscarTarefaEspecifica();
      }
    }
  }, [location.search, location.pathname, tarefas, navigate]);

  const abrirDialog = (tarefa = null) => {
    if (tarefa) {
      setEditingTarefa(tarefa);
      setFormData({
        cliente_id: tarefa.cliente_id,
        descricao: tarefa.descricao,
        data_vencimento: new Date(tarefa.data_vencimento + "T00:00:00"),
        hora_vencimento: new Date(`2000-01-01T${tarefa.hora_vencimento}`),
        status: tarefa.status,
        responsavel_id: tarefa.responsavel_id || "",
        priorizar: tarefa.priorizar || false,
        comentarios: tarefa.comentarios || "",
      });
    } else {
      setEditingTarefa(null);
      setFormData({
        cliente_id: "",
        descricao: "",
        data_vencimento: new Date(),
        hora_vencimento: new Date(),
        status: "pendente",
        responsavel_id: "",
        priorizar: false,
        comentarios: "",
      });
    }
    setOpenDialog(true);
  };

  const fecharDialog = () => {
    setOpenDialog(false);
    setEditingTarefa(null);
    setFormData({
      cliente_id: "",
      descricao: "",
      data_vencimento: new Date(),
      hora_vencimento: new Date(),
      status: "pendente",
      responsavel_id: "",
      priorizar: false,
      comentarios: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (salvando) return; // Evita cliques duplos

    if (
      !formData.cliente_id ||
      !formData.descricao ||
      !formData.data_vencimento ||
      !formData.hora_vencimento
    ) {
      mostrarSnackbar("Todos os campos são obrigatórios", "error");
      return;
    }

    try {
      setSalvando(true);
      // Função para formatar data sem problemas de timezone
      const formatarDataLocal = (data) => {
        const ano = data.getFullYear();
        const mes = String(data.getMonth() + 1).padStart(2, "0");
        const dia = String(data.getDate()).padStart(2, "0");
        return `${ano}-${mes}-${dia}`;
      };

      const dadosEnvio = {
        ...formData,
        data_vencimento: formatarDataLocal(formData.data_vencimento),
        hora_vencimento: formData.hora_vencimento.toTimeString().slice(0, 5),
      };

      if (editingTarefa) {
        await tarefaService.update(editingTarefa.id, dadosEnvio);
        mostrarSnackbar("Tarefa atualizada com sucesso", "success");
      } else {
        await tarefaService.create(dadosEnvio);
        mostrarSnackbar("Tarefa criada com sucesso", "success");
      }

      fecharDialog();
      carregarTarefas();

      // Se veio da agenda, voltar para lá
      if (location.search.includes("edit")) {
        window.history.replaceState({}, "", "/agenda");
        window.location.href = "/agenda";
      }
    } catch (error) {
      console.error("Erro ao salvar tarefa:", error);
      mostrarSnackbar("Erro ao salvar tarefa", "error");
    } finally {
      setSalvando(false);
    }
  };

  const excluirTarefa = (tarefa) => {
    const descricao = `${tarefa.Cliente?.nome || "Cliente"} - ${tarefa.descricao}`;
    setConfirmacaoExclusao({
      aberto: true,
      tarefaId: tarefa.id,
      tarefaDescricao: descricao,
    });
  };

  const confirmarExclusao = async () => {
    if (excluindo) return; // Evita cliques duplos

    try {
      setExcluindo(true);
      await tarefaService.delete(confirmacaoExclusao.tarefaId);
      mostrarSnackbar("Tarefa excluída com sucesso", "success");
      setConfirmacaoExclusao({
        aberto: false,
        tarefaId: null,
        tarefaDescricao: "",
      });
      carregarTarefas();
    } catch (error) {
      console.error("Erro ao excluir tarefa:", error);
      mostrarSnackbar("Erro ao excluir tarefa", "error");
    } finally {
      setExcluindo(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await tarefaService.updateStatus(id, newStatus);
      mostrarSnackbar("Status atualizado com sucesso", "success");
      carregarTarefas();
    } catch (error) {
      console.error("Erro ao atualizar status:", error);
      mostrarSnackbar("Erro ao atualizar status", "error");
    }
  };

  const handlePriorizarChange = async (id, priorizar) => {
    try {
      await tarefaService.updatePriorizar(id, priorizar);
      mostrarSnackbar("Priorização atualizada com sucesso", "success");
      carregarTarefas();
    } catch (error) {
      console.error("Erro ao atualizar priorização:", error);
      mostrarSnackbar("Erro ao atualizar priorização", "error");
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPaginacao((prev) => ({ ...prev, page: 0 }));
  };

  const clearFilters = () => {
    setFilters({
      cliente_id: "",
      status: "pendente", // Manter filtro padrão para tarefas pendentes
      data_inicio: "",
      data_fim: "",
      priorizar: "",
      responsavel_id: "",
      escritorio_id_filter: "",
    });
    setPaginacao((prev) => ({ ...prev, page: 0 }));
  };

  const mostrarSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const fecharSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pendente":
        return "warning";
      case "em_andamento":
        return "primary";
      case "enviado":
        return "info";
      case "enviado_atrasado":
        return "error";
      case "pago":
        return "success";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "pendente":
        return "Pendente";
      case "em_andamento":
        return "Em andamento";
      case "enviado":
        return "Enviado";
      case "enviado_atrasado":
        return "Enviado com atraso";
      case "pago":
        return "Pago";
      default:
        return status;
    }
  };

  const formatarData = (data) => {
    // Garantir que a data seja interpretada como local, não UTC
    const dataLocal = new Date(data + "T00:00:00");
    return dataLocal.toLocaleDateString("pt-BR");
  };

  const formatarHora = (hora) => {
    return hora;
  };

  const getClienteNome = (clienteId) => {
    const cliente = clientes.find((c) => c.id === clienteId);
    return cliente
      ? cliente.nome || cliente.fantasia
      : "Cliente não encontrado";
  };

  // Filtrar tarefas
  const tarefasFiltradas = tarefas.filter((tarefa) => {
    if (filters.status && tarefa.status !== filters.status) return false;
    if (
      filters.cliente_id &&
      tarefa.cliente_id !== parseInt(filters.cliente_id)
    )
      return false;

    // Filtro por responsável
    if (
      filters.responsavel_id &&
      tarefa.responsavel_id !== parseInt(filters.responsavel_id)
    ) {
      return false;
    }

    // Filtro por priorizar
    if (
      filters.priorizar !== "" &&
      tarefa.priorizar !== (filters.priorizar === "true")
    ) {
      return false;
    }

    // Filtro por data de vencimento
    if (filters.data_inicio || filters.data_fim) {
      const dataVencimento = new Date(tarefa.data_vencimento + "T00:00:00");
      if (filters.data_inicio && dataVencimento < new Date(filters.data_inicio))
        return false;
      if (filters.data_fim && dataVencimento > new Date(filters.data_fim))
        return false;
    }

    return true;
  });

  if (loading) {
    return (
      <Box>
        <Typography variant="h6" sx={{ mt: 2 }}>
          Carregando tarefas...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 600, color: "#333" }}>
          Tarefas
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{ mr: 2 }}
          >
            Filtros
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => abrirDialog()}
            sx={{ backgroundColor: "#1976d2" }}
          >
            Nova tarefa
          </Button>
        </Box>
      </Box>

      {/* Filtros expandidos */}
      {showFilters && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Filtros
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              width: "100%",
              flexWrap: { xs: "wrap", md: "nowrap" },
              alignItems: "center",
            }}
          >
            {/* Cliente */}
            <Box
              sx={{ flex: "0 1 25%", minWidth: { xs: "100%", md: "200px" } }}
            >
              <TextField
                fullWidth
                select
                label="Cliente"
                value={filters.cliente_id}
                onChange={(e) =>
                  handleFilterChange("cliente_id", e.target.value)
                }
              >
                <MenuItem value="">Todos</MenuItem>
                {clientes.map((cliente) => (
                  <MenuItem key={cliente.id} value={cliente.id}>
                    {cliente.nome || cliente.fantasia}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Status */}
            <Box
              sx={{ flex: "0 1 20%", minWidth: { xs: "100%", md: "150px" } }}
            >
              <TextField
                fullWidth
                select
                label="Status"
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="pendente">Pendente</MenuItem>
                <MenuItem value="em_andamento">Em andamento</MenuItem>
                <MenuItem value="enviado">Enviado</MenuItem>
                <MenuItem value="enviado_atrasado">Enviado com atraso</MenuItem>
                <MenuItem value="pago">Pago</MenuItem>
              </TextField>
            </Box>

            {/* Priorizar */}
            <Box
              sx={{ flex: "0 1 20%", minWidth: { xs: "100%", md: "150px" } }}
            >
              <TextField
                fullWidth
                select
                label="Priorizar"
                value={filters.priorizar}
                onChange={(e) =>
                  handleFilterChange("priorizar", e.target.value)
                }
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="true">Priorizadas</MenuItem>
                <MenuItem value="false">Não priorizadas</MenuItem>
              </TextField>
            </Box>

            {/* Data Início */}
            <Box
              sx={{ flex: "0 1 15%", minWidth: { xs: "100%", md: "150px" } }}
            >
              <TextField
                fullWidth
                label="Data início"
                type="date"
                value={filters.data_inicio}
                onChange={(e) =>
                  handleFilterChange("data_inicio", e.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            {/* Data Fim */}
            <Box
              sx={{ flex: "0 1 15%", minWidth: { xs: "100%", md: "150px" } }}
            >
              <TextField
                fullWidth
                label="Data fim"
                type="date"
                value={filters.data_fim}
                onChange={(e) => handleFilterChange("data_fim", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            {/* Responsável */}
            <Box
              sx={{ flex: "0 1 20%", minWidth: { xs: "100%", md: "200px" } }}
            >
              <TextField
                fullWidth
                select
                label="Responsável"
                value={filters.responsavel_id}
                onChange={(e) =>
                  handleFilterChange("responsavel_id", e.target.value)
                }
              >
                <MenuItem value="">Todos</MenuItem>
                {usuarios.map((usuario) => (
                  <MenuItem key={usuario.id} value={usuario.id}>
                    {usuario.nome}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Filtro de Escritório - apenas para super admin */}
            {user?.is_super_admin && (
              <Box
                sx={{ flex: "0 1 25%", minWidth: { xs: "100%", md: "200px" } }}
              >
                <TextField
                  fullWidth
                  select
                  label="Escritório"
                  value={filters.escritorio_id_filter}
                  onChange={(e) =>
                    handleFilterChange("escritorio_id_filter", e.target.value)
                  }
                >
                  <MenuItem value="">Todos os escritórios</MenuItem>
                  {escritorios.map((escritorio) => (
                    <MenuItem key={escritorio.id} value={escritorio.id}>
                      {escritorio.nome}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            )}
          </Box>

          <Box mt={3}>
            <Button onClick={clearFilters} variant="outlined" size="small">
              Redefinir filtros
            </Button>
          </Box>
        </Paper>
      )}

      {/* Tabela de tarefas */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Descrição</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Vencimento</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Hora</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Responsável</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Priorizar</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Comentários</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Data do envio</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tarefasFiltradas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    Nenhuma tarefa encontrada
                  </TableCell>
                </TableRow>
              ) : (
                tarefasFiltradas.map((tarefa) => (
                  <TableRow key={tarefa.id}>
                    <TableCell>{getClienteNome(tarefa.cliente_id)}</TableCell>
                    <TableCell>{tarefa.descricao}</TableCell>
                    <TableCell>
                      {formatarData(tarefa.data_vencimento)}
                    </TableCell>
                    <TableCell>
                      {formatarHora(tarefa.hora_vencimento)}
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={tarefa.status}
                          onChange={(e) =>
                            handleStatusChange(tarefa.id, e.target.value)
                          }
                          displayEmpty
                          renderValue={(selected) => (
                            <Chip
                              label={getStatusLabel(selected)}
                              color={getStatusColor(selected)}
                              size="small"
                            />
                          )}
                        >
                          <MenuItem value="pendente">Pendente</MenuItem>
                          <MenuItem value="em_andamento">Em andamento</MenuItem>
                          <MenuItem value="enviado">Enviado</MenuItem>
                          <MenuItem value="enviado_atrasado">
                            Enviado com atraso
                          </MenuItem>
                          <MenuItem value="pago">Pago</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      {tarefa.responsavel ? tarefa.responsavel.nome : "Nenhum"}
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={tarefa.priorizar || false}
                        onChange={(e) =>
                          handlePriorizarChange(tarefa.id, e.target.checked)
                        }
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>
                      {tarefa.comentarios ? (
                        <Tooltip title={tarefa.comentarios} placement="top">
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 150,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              cursor: "pointer",
                            }}
                          >
                            {tarefa.comentarios}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Sem comentários
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {tarefa.data_envio ? (
                        <Box>
                          <Typography variant="body2">
                            {formatarData(tarefa.data_envio)}
                          </Typography>
                          {tarefa.enviado_por ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.7rem" }}
                            >
                              por {tarefa.enviado_por.nome}
                            </Typography>
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.7rem", fontStyle: "italic" }}
                            >
                              (usuário não identificado)
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Não enviado
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => abrirDialog(tarefa)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => excluirTarefa(tarefa)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginação */}
        <TablePagination
          component="div"
          count={paginacao.total}
          page={paginacao.page}
          onPageChange={(event, newPage) =>
            setPaginacao((prev) => ({ ...prev, page: newPage }))
          }
          rowsPerPage={paginacao.limit}
          onRowsPerPageChange={(event) => {
            setPaginacao((prev) => ({
              ...prev,
              limit: parseInt(event.target.value, 10),
              page: 0,
            }));
          }}
          labelRowsPerPage="Itens por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count}`
          }
        />
      </Card>

      {/* Modal para criar/editar tarefa */}
      <TarefaModal
        open={openDialog}
        onClose={fecharDialog}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        clientes={clientes}
        usuarios={usuarios}
        usuariosEscritorio={usuarios}
        editingTarefa={editingTarefa}
        salvando={salvando}
      />

      {/* Modal de confirmação de exclusão */}
      <Dialog
        open={confirmacaoExclusao.aberto}
        onClose={() =>
          setConfirmacaoExclusao({
            aberto: false,
            tarefaId: null,
            tarefaDescricao: "",
          })
        }
      >
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir a tarefa "
            {confirmacaoExclusao.tarefaDescricao}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmacaoExclusao({
                aberto: false,
                tarefaId: null,
                tarefaDescricao: "",
              })
            }
            color="primary"
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmarExclusao}
            color="error"
            variant="contained"
            disabled={excluindo}
          >
            {excluindo ? <CircularProgress size={20} /> : "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mensagens */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={fecharSnackbar}
      >
        <Alert onClose={fecharSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Tarefas;
